import { useCallback } from "react";
import { container } from "tsyringe";
import { XMarkIcon } from "@heroicons/react/20/solid";
import ProjectTeamRoleContributorsRequestResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsRequestResource";
import ProjectTeamRoleContributorsResponseResource from "common/resources/Project/Config/Team/Role/ProjectTeamRoleContributorsResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import Modal from "components/elements/Modal";
import Typography from "components/elements/Typography";
import Form, { useForm } from "components/materials/Form";
import Input from "components/materials/Form/InputElement";
import I18n from "components/materials/I18n";
import { useProjectTeamContext } from "components/pages/ProfessionalSpace/contexts/ProjectTeam";
import { useProjectId } from "hooks/useProjectId";
import RoleService from "services/projects/RoleService";
import classes from "./classes.module.scss";

type IProps = {
	isOpen: boolean;
	onClose: () => void;
	onRoleCreated: (role: ProjectTeamRoleContributorsResponseResource) => void;
};

const roleService = container.resolve(RoleService);

export default function CreateRoleModal(props: IProps) {
	const { projectId } = useProjectId();
	const { projectTeamId } = useProjectTeamContext();
	const { errors, handleError, clearErrors } = useForm();

	const onCreateRole = useCallback(
		(formData: { [key: string]: unknown }) => {
			clearErrors();

			const roleRequestResource = ProjectTeamRoleContributorsRequestResource.hydrate<ProjectTeamRoleContributorsRequestResource>({
				projectTeamId,
				roleTitle: [{ lang: I18n.getLang(), text: formData["roleTitle"] as string }],
				index: parseInt(formData["index"] as string),
				contributors: [],
			});

			roleRequestResource
				.validateOrReject()
				.then(() => roleService.create(projectId, projectTeamId, roleRequestResource))
				.then(props.onRoleCreated)
				.catch(handleError);
		},
		[clearErrors, handleError, projectId, projectTeamId, props.onRoleCreated],
	);

	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} className={classes["root"]}>
			<div className={classes["header"]}>
				<Typography type="p" size="large" weight="bold">
					{I18n.asset.pages.professional_space.tabs.team.create_role_modal.title}
				</Typography>
				<XMarkIcon className={classes["icon"]} onClick={props.onClose} />
			</div>

			<Form onSubmit={onCreateRole} errors={errors} className={classes["form-container"]}>
				<Input
					name="roleTitle"
					required
					label={I18n.asset.pages.professional_space.tabs.team.create_role_modal.role_name}
					additionalInfo={I18n.asset.pages.professional_space.tabs.team.create_role_modal.role_name_max_length}
				/>
				<Input name="index" type="number" min="1" max="1000" label={I18n.asset.pages.professional_space.tabs.team.create_role_modal.role_order} defaultValue={"1"} />
				<div className={classes["buttons-container"]}>
					<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} disabled>
						{I18n.asset.pages.professional_space.tabs.team.edit_role_modal.add_member}
					</Button>
					<Button type="submit" variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY}>
						{I18n.asset.pages.professional_space.tabs.team.create_role_modal.create_role}
					</Button>
				</div>
			</Form>
		</Modal>
	);
}
